import type { MsFilters } from "../types";

export const MANAGED_SERVICE_CONTEXT_STORAGE_KEY = "managed-service-context-change";

const keys = {
    accountId: "msAccountId",
    companyId: "msCompanyId",
    companyName: "msCompanyName",
    since: "msSince",
    until: "msUntil",
    formPack: "msFormPack",
    status: "msStatus",
    humanOutcome: "msHumanOutcome",
    validatedOn: "msValidatedOn",
    certificateIds: "msCertIds",
    agenticStatus: "msAgenticStatus",
    region: "msRegion",
    reason: "msReason",
    // Certificates-tab Form pack multi-select (distinct from Worklist's single msFormPack).
    certFormPack: "msCertFormPack",
} as const;

const managedKeys = Object.values(keys);

export interface ManagedServiceCertificateFilters {
    accountId: string;
    companyId: string;
    companyName: string;
    statuses: string[];
    certificateIds: string[];
    agenticStatuses: string[];
    regionIds: string[];
    reasonIds: string[];
    formPacks: string[];
}

export const broadcastManagedServiceContextChange = (
    accountId: string,
    companyId: string
): void => {
    window.localStorage.setItem(
        MANAGED_SERVICE_CONTEXT_STORAGE_KEY,
        JSON.stringify({ accountId, companyId, changedAt: Date.now() })
    );
};

/**
 * A context switch reloads every ECM tab, so a second switch fired while that is
 * still settling can have its cookie write land after the newer one. Hold the
 * scope controls for this long afterwards rather than trying to order the writes.
 */
export const SWITCH_COOLDOWN_MS = 7000;

/**
 * Milliseconds left before another context switch is allowed, derived from the
 * timestamp the last successful switch recorded. Returns 0 — never blocking —
 * when storage is unavailable or the stored value is unusable: a storage problem
 * must not lock someone out of switching.
 */
export const readSwitchCooldownRemaining = (): number => {
    try {
        const raw = window.localStorage.getItem(MANAGED_SERVICE_CONTEXT_STORAGE_KEY);
        if (!raw) {
            return 0;
        }
        const { changedAt } = JSON.parse(raw) as { changedAt?: unknown };
        if (typeof changedAt !== "number" || !Number.isFinite(changedAt)) {
            return 0;
        }
        return Math.max(0, SWITCH_COOLDOWN_MS - (Date.now() - changedAt));
    } catch {
        return 0;
    }
};

export const reloadWithManagedServiceContext = (url: string): void => {
    window.location.replace(url);
};

export const reloadForManagedServiceContextChange = (): void => {
    window.location.reload();
};

const replaceManagedParams = (search: string): URLSearchParams => {
    const params = new URLSearchParams(search);
    managedKeys.forEach(key => params.delete(key));
    return params;
};

const append = (params: URLSearchParams, key: string, value?: string): void => {
    if (value) {
        params.set(key, value);
    }
};

const appendMany = (params: URLSearchParams, key: string, values: string[]): void => {
    values.filter(Boolean).forEach(value => params.append(key, value));
};

const toUrl = (pathname: string, params: URLSearchParams): string => {
    const serialized = params.toString();
    return serialized ? `${pathname}?${serialized}` : pathname;
};

export const readWorklistUrlState = (search: string): MsFilters => {
    const params = new URLSearchParams(search);
    const statuses = params.getAll(keys.status);
    const humanOutcomes = params.getAll(keys.humanOutcome);
    const certificateIds = params.getAll(keys.certificateIds);
    return {
        accountId: params.get(keys.accountId) ?? undefined,
        companyId: params.get(keys.companyId) ?? undefined,
        companyName: params.get(keys.companyName) ?? undefined,
        since: params.get(keys.since) ?? undefined,
        until: params.get(keys.until) ?? undefined,
        formPack: params.get(keys.formPack) ?? undefined,
        statuses: statuses.length > 0 ? statuses : undefined,
        humanOutcomes: humanOutcomes.length > 0 ? humanOutcomes : undefined,
        validatedOn: params.get(keys.validatedOn) ?? undefined,
        certificateIds: certificateIds.length > 0 ? certificateIds : undefined,
    };
};

export const buildWorklistUrl = (pathname: string, search: string, filters: MsFilters): string => {
    const params = replaceManagedParams(search);
    append(params, keys.accountId, filters.accountId);
    append(params, keys.companyId, filters.companyId);
    append(params, keys.companyName, filters.companyName);
    append(params, keys.since, filters.since);
    append(params, keys.until, filters.until);
    append(params, keys.formPack, filters.formPack);
    appendMany(params, keys.status, filters.statuses ?? []);
    appendMany(params, keys.humanOutcome, filters.humanOutcomes ?? []);
    append(params, keys.validatedOn, filters.validatedOn);
    appendMany(params, keys.certificateIds, filters.certificateIds ?? []);
    return toUrl(pathname, params);
};

export const readCertificatesUrlState = (search: string): ManagedServiceCertificateFilters => {
    const params = new URLSearchParams(search);
    return {
        accountId: params.get(keys.accountId) ?? "",
        companyId: params.get(keys.companyId) ?? "",
        companyName: params.get(keys.companyName) ?? "",
        statuses: params.getAll(keys.status),
        certificateIds: params.getAll(keys.certificateIds),
        agenticStatuses: params.getAll(keys.agenticStatus),
        regionIds: params.getAll(keys.region),
        reasonIds: params.getAll(keys.reason),
        formPacks: params.getAll(keys.certFormPack),
    };
};

export const buildCertificatesUrl = (
    pathname: string,
    search: string,
    filters: ManagedServiceCertificateFilters
): string => {
    const params = replaceManagedParams(search);
    append(params, keys.accountId, filters.accountId);
    append(params, keys.companyId, filters.companyId);
    append(params, keys.companyName, filters.companyName);
    appendMany(params, keys.status, filters.statuses);
    appendMany(params, keys.certificateIds, filters.certificateIds);
    appendMany(params, keys.agenticStatus, filters.agenticStatuses);
    appendMany(params, keys.region, filters.regionIds);
    appendMany(params, keys.reason, filters.reasonIds);
    appendMany(params, keys.certFormPack, filters.formPacks);
    return toUrl(pathname, params);
};
